import React, { useCallback, useEffect, useState } from 'react';

const getVisibleChildren = (element: HTMLElement | null) => {
  if (!element?.children) {
    return [];
  }

  return Array.from(element.children)?.filter((e) => (e as HTMLElement).offsetParent !== null);
};

const isLastChildVisible = (currentScroll: number, element: HTMLElement | null) => {
  if (!element) {
    return true;
  }

  const width = element?.clientWidth || 0;
  const children = getVisibleChildren(element);

  if (!children.length) {
    return false;
  }

  const childWidth = children[0].clientWidth;
  return (currentScroll + width) / childWidth >= children.length;
};

interface IOptions<TContainer extends HTMLElement> {
  scrollBehavior?: ScrollBehavior;
  container: React.MutableRefObject<TContainer | null>;
  scrollType?: 'container' | 'item';
}

let scrollTimer: ReturnType<typeof setTimeout>;

export default <TContainer extends HTMLElement>(options: IOptions<TContainer>, deps?: any[]) => {
  const [scroll, setScroll] = useState(0);
  const [hideNext, setHideNext] = useState(isLastChildVisible(scroll, options.container.current));
  const [hidePrev, setHidePrev] = useState(scroll <= 0);

  useEffect(() => {
    setScroll(0);
  }, [...(deps || [])]);

  useEffect(() => {
    options.container.current?.scrollTo({
      left: scroll,
      top: 0,
      behavior: options.scrollBehavior,
    });
    setHideNext(isLastChildVisible(scroll, options.container.current));
    setHidePrev(scroll <= 0);
  }, [scroll]);

  const next = useCallback(() => {
    const sliderWidth = options.container.current?.clientWidth || 0;
    const value = scroll + sliderWidth;

    const next = isLastChildVisible(scroll, options.container.current) ? 0 : value;
    setScroll(next);
  }, [scroll]);

  useEffect(() => {
    const handleScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        setScroll(options.container.current?.scrollLeft || 0);
      }, 100);
    };

    options.container.current?.addEventListener('scroll', handleScroll);

    return () => {
      options.container.current?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const prev = useCallback(() => {
    const sliderWidth = options.container.current?.clientWidth || 0;
    const value = scroll - sliderWidth;
    const next = value < 0 ? 0 : value;
    setScroll(next);
  }, [scroll]);

  useEffect(() => {
    setHideNext(isLastChildVisible(scroll, options.container.current));
  }, [...(deps || [])]);

  return {
    hideNext,
    hidePrev,
    next,
    prev,
  };
};
